import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font } from "@realtyflows/ui";
import { getPublicStats, getPublicTestimonials } from "../api";
import useResponsive from "../hooks/useResponsive";
import { ScrollReveal, AnimatedCounter } from "../hooks/useScrollReveal";

function PricingCard({ plan, navigate, isAnnual, isMobile }) {
  const monthlyNum = parseInt(plan.price.replace(/[$,]/g, ""));
  const displayPrice = isAnnual && !plan.contactSales ? `$${Math.round(monthlyNum * 10 / 12).toLocaleString()}` : plan.price;
  return (
    <div style={{
      background: plan.featured ? t.navyLight : t.surface,
      border: plan.featured ? `2px solid ${t.gold}` : `1px solid ${t.navyLight}20`,
      padding: isMobile ? "28px 20px" : "40px 28px", position: "relative",
      display: "flex", flexDirection: "column",
      boxShadow: plan.featured ? `0 16px 48px ${t.navyLight}25` : "none",
      transform: plan.featured && !isMobile ? "scale(1.03)" : "none",
      zIndex: plan.featured ? 2 : 1,
      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = plan.featured && !isMobile ? "scale(1.05) translateY(-4px)" : "translateY(-4px)";
        e.currentTarget.style.boxShadow = `0 20px 60px rgba(0,0,0,0.15)`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = plan.featured && !isMobile ? "scale(1.03)" : "none";
        e.currentTarget.style.boxShadow = plan.featured ? `0 16px 48px ${t.navyLight}25` : "none";
      }}
    >
      <div style={{
        fontSize: 11, fontWeight: 600, letterSpacing: "3px", textTransform: "uppercase",
        color: t.gold, fontFamily: font.body, marginBottom: 16,
      }}>{plan.tier}</div>
      <div style={{ marginBottom: isAnnual && !plan.contactSales ? 6 : 20 }}>
        <span style={{
          fontFamily: font.display, fontSize: isMobile ? 36 : 44, fontWeight: 300,
          color: plan.featured ? "#fff" : t.navyLight,
        }}>{displayPrice}</span>
        <span style={{
          fontSize: 13, fontWeight: 400, color: plan.featured ? "rgba(255,255,255,0.5)" : t.textMuted,
          fontFamily: font.body,
        }}>/mo</span>
      </div>
      {isAnnual && !plan.contactSales && (
        <div style={{
          fontSize: 11, fontWeight: 600, marginBottom: 16,
          color: plan.featured ? t.gold : t.emerald, fontFamily: font.body,
        }}>
          ${(monthlyNum * 10).toLocaleString()}/yr — Save ${(monthlyNum * 2).toLocaleString()}
        </div>
      )}
      <div style={{ width: 40, height: 1, background: t.gold, marginBottom: 22 }} />
      <div style={{ flex: 1 }}>
        {plan.features.map(feature => (
          <div key={feature} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
            <span style={{ color: t.gold, fontSize: 11, marginTop: 2, fontFamily: font.body }}>{"\u2014"}</span>
            <span style={{
              fontSize: 12, fontWeight: 300, color: plan.featured ? "rgba(255,255,255,0.75)" : t.textSecondary,
              fontFamily: font.body, lineHeight: 1.5,
            }}>{feature}</span>
          </div>
        ))}
      </div>
      <button onClick={() => plan.contactSales ? navigate("/contact?plan=enterprise") : navigate(`/signup?plan=${plan.planKey}`)} style={{
        marginTop: 28, width: "100%", padding: "14px 0",
        fontSize: 11, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase",
        cursor: "pointer", fontFamily: font.body, transition: "all 0.3s",
        ...(plan.btnVariant === "outline" ? {
          background: "transparent", border: `1px solid ${t.gold}`, color: t.gold,
        } : plan.btnVariant === "gold" ? {
          background: `linear-gradient(135deg, ${t.gold}, ${t.goldLight})`, border: "none", color: "#fff",
        } : {
          background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`, border: "none", color: "#fff",
        }),
      }}
        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.15)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
      >
        {plan.contactSales ? "Contact Sales" : plan.featured ? "Start Pro" : "Get Started"}
      </button>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;
  const isTablet = responsive.width >= 640 && responsive.width < 1024;
  const isCompact = responsive.width < 1024;
  const isDesktop = responsive.width >= 1024;
  const [liveStats, setLiveStats] = useState(null);
  const [liveTestimonials, setLiveTestimonials] = useState(null);
  const [pricingInterval, setPricingInterval] = useState("monthly");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [comparePosition, setComparePosition] = useState(50);
  const isAnnual = pricingInterval === "annual";

  // Responsive helpers
  const sectionPadding = isMobile ? "60px 16px" : isTablet ? "80px 32px" : "100px 64px";
  const sectionPaddingSmall = isMobile ? "40px 16px" : isTablet ? "48px 32px" : "64px 64px";

  const WaveDivider = ({ color = t.pageBg, flip = false }) => (
    <div style={{ marginTop: flip ? 0 : -1, marginBottom: flip ? -1 : 0, lineHeight: 0, transform: flip ? "rotate(180deg)" : "none" }}>
      <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ width: "100%", height: isMobile ? 30 : 60, display: "block" }}>
        <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,20 1440,30 L1440,60 L0,60 Z" fill={color} />
      </svg>
    </div>
  );

  useEffect(() => {
    document.title = "RealtyFlows — AI-Powered CRM for Real Estate Agents";
    getPublicStats().then(setLiveStats).catch(() => {});
    getPublicTestimonials().then(t => { if (t?.length > 0) setLiveTestimonials(t); }).catch(() => {});
    return () => { document.title = "RealtyFlows"; };
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    if (!isCompact) setMobileMenuOpen(false);
  }, [isCompact]);

  const navLinks = [
    { label: "Features", target: "features" },
    { label: "Pricing", target: "pricing" },
    { label: "FAQ", target: "faq" },
    { label: "Contact", href: "/contact" },
  ];

  const marqueeItems = [
    "Buyer Pipeline", "AI Automations", "Gmail Integration", "Client Scoring",
    "Smart Scheduling", "Offer Management", "Market Analytics", "Client Portal",
    "Document Vault", "Commission Tracker", "Free CRM", "Agent-to-Agent Network",
  ];

  const features = [
    { num: "01", icon: pipelineIcon, title: "Real Estate Pipeline Management", desc: "Drag-and-drop Kanban boards crafted to mirror your exact workflow. Our real estate pipeline management software tracks every deal from first inquiry to closing table." },
    { num: "02", icon: aiIcon, title: "AI Lead Follow-Up", desc: "An intelligent concierge that responds to clients in under four minutes, qualifies buyers, and schedules showings autonomously — AI lead follow-up built for real estate agents." },
    { num: "03", icon: analyticsIcon, title: "Lead Scoring & Analytics", desc: "Real estate lead scoring software that distills complex data into actionable intelligence. AI scores every lead so you focus on clients most likely to close." },
    { num: "04", icon: automationIcon, title: "Automated Follow-Up", desc: "Sophisticated automated follow-up workflows for real estate that orchestrate reminders, drip sequences, and nurture campaigns — so no lead ever slips through the cracks." },
    { num: "05", icon: integrationIcon, title: "Seamless Integrations", desc: "Native connections to Gmail, Outlook, Zillow, and forty-plus platforms ensuring your real estate agent CRM software ecosystem works in perfect concert." },
    { num: "06", icon: agentNetworkIcon, title: "Agent-to-Agent Network", desc: "The only CRM for realtors with a built-in agent-to-agent network. Refer clients, co-broke deals, and grow your referral business from within the platform." },
    { num: "07", icon: freeIcon, title: "Free CRM for Realtors", desc: "A completely free real estate CRM with unlimited clients, visual pipeline, and deal tracking. No trial period. No credit card. The best free CRM for real estate agents." },
    { num: "08", icon: securityIcon, title: "Bank-Grade Security", desc: "Enterprise-level encryption and compliance protocols safeguarding every transaction and client communication. Your data stays yours — always." },
  ];

  const hasRealData = liveStats && liveStats.contracts_managed > 0;

  const defaultPipeline = [
    { emoji: "\u{1F4E9}", name: "Inquiry", count: 24, value: "$4.2M" },
    { emoji: "\u{1F50D}", name: "Active Search", count: 18, value: "$6.8M" },
    { emoji: "\u{1F4DD}", name: "Offer Made", count: 7, value: "$3.1M" },
    { emoji: "\u{1F4CB}", name: "Under Contract", count: 5, value: "$2.4M" },
    { emoji: "\u{1F3E0}", name: "Closed", count: 12, value: "$8.9M" },
  ];
  const pipelineStages = (hasRealData && liveStats.pipeline_stages?.length > 0)
    ? liveStats.pipeline_stages
    : defaultPipeline;

  const soloAgentPlans = [
    {
      tier: "Starter",
      price: "$39",
      period: "/month",
      features: ["3 signings/month", "1 user", "Basic AI", "Pipeline view", "Email support"],
      btnVariant: "outline",
      planKey: "starter",
    },
    {
      tier: "Pro",
      price: "$89",
      period: "/month",
      featured: true,
      features: ["10 signings/month", "1 user", "Full AI", "Smart automation", "Priority email support"],
      btnVariant: "gold",
      planKey: "pro",
    },
  ];

  const teamPricingPlans = [
    {
      tier: "Team Starter",
      price: "$249",
      period: "/month",
      features: ["20 signings/month", "5 users included", "Full AI", "Team management", "+$29/extra seat"],
      btnVariant: "outline",
      planKey: "team_starter",
    },
    {
      tier: "Team Pro",
      price: "$549",
      period: "/month",
      featured: true,
      features: ["50 signings/month", "15 users included", "Advanced AI", "Brokerage pipeline", "+$29/extra seat"],
      btnVariant: "gold",
      planKey: "team_pro",
    },
    {
      tier: "Agency",
      price: "$999",
      period: "/month",
      features: ["100 signings/month", "50 users included", "Advanced AI + White Label", "Custom branding", "+$19/extra seat"],
      btnVariant: "navy",
      planKey: "agency",
    },
    {
      tier: "Enterprise",
      price: "Contact Sales",
      period: "",
      features: ["Custom signings", "Custom users", "Custom AI", "API & webhooks", "24/7 support"],
      btnVariant: "navy",
      planKey: "enterprise",
      contactSales: true,
    },
  ];

  const defaultTestimonials = [
    { quote: "RealtyFlows transformed how our brokerage operates. The pipeline visibility alone has increased our close rate by thirty percent.", author: "Sarah Mitchell", role: "Managing Broker, Prestige Realty", avatar: "SM", rating: 5, date: "Feb 28, 2026" },
    { quote: "The AI concierge responds to my clients faster than I ever could. It's like having a full-time assistant who never sleeps.", author: "James Chen", role: "Top Producer, Sotheby's", avatar: "JC", rating: 5, date: "Mar 2, 2026" },
    { quote: "Finally, a platform that matches the caliber of properties we represent. Elegant, powerful, and remarkably intuitive.", author: "Victoria Lane", role: "Luxury Specialist, Christie's", avatar: "VL", rating: 5, date: "Mar 8, 2026" },
  ];
  const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const allTestimonials = liveTestimonials
    ? liveTestimonials.map(lt => ({ quote: lt.quote, author: lt.author, role: lt.role || "", avatar: lt.avatar || lt.author?.split(" ").map(w => w[0]).join(""), rating: lt.rating, date: formatDate(lt.created_at) }))
    : defaultTestimonials;

  const [testimonialPage, setTestimonialPage] = useState(0);
  const perPage = isMobile ? 1 : 3;
  const totalPages = Math.max(1, Math.ceil(allTestimonials.length / perPage));
  const testimonials = allTestimonials.slice(testimonialPage * perPage, testimonialPage * perPage + perPage);

  // Auto-rotate testimonials every 6 seconds
  useEffect(() => {
    if (totalPages <= 1) return;
    const timer = setInterval(() => {
      setTestimonialPage(p => (p + 1) % totalPages);
    }, 6000);
    return () => clearInterval(timer);
  }, [totalPages]);

  const getTimeAgo = (time) => {
    const diff = Date.now() - new Date(time).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const stats = hasRealData ? [
    { value: liveStats.contracts_managed.toLocaleString(), rawValue: liveStats.contracts_managed, label: "Contracts Managed", icon: "\uD83D\uDCC4" },
    { value: liveStats.pipeline_value, label: "In Active Pipeline", icon: "\uD83C\uDFE0" },
    { value: `${liveStats.agents_count}`, rawValue: liveStats.agents_count, label: "Active Agents", icon: "\uD83D\uDC65" },
    { value: liveStats.messages_sent.toLocaleString(), rawValue: liveStats.messages_sent, label: "AI Messages Sent", icon: "\u26A1" },
  ] : [];

  const defaultFeed = [
    { action: "A contract was sent in Lincoln Park", mins: 0 },
    { action: "A new lead was qualified in Schaumburg", mins: 2 },
    { action: "A deal was closed in Naperville", mins: 5 },
    { action: "A showing was scheduled in Evanston", mins: 11 },
    { action: "A contract was sent in River North", mins: 18 },
  ];

  const activityFeed = (hasRealData && liveStats.activity_feed?.length > 0)
    ? liveStats.activity_feed.slice(0, 5).map(item => ({ action: item.action, ago: getTimeAgo(item.time) }))
    : defaultFeed.map(item => ({ action: item.action, ago: item.mins === 0 ? "just now" : `${item.mins} min ago` }));

  // Compute feature grid columns
  const featureGridCols = isMobile ? "1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(4, 1fr)";

  // Compute pricing grid columns
  const soloGridCols = isMobile ? "1fr" : "repeat(2, 1fr)";
  const teamGridCols = isMobile ? "1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(4, 1fr)";

  // Compute testimonial grid columns
  const testimonialGridCols = isMobile ? "1fr" : isTablet ? "1fr" : "repeat(3, 1fr)";

  // Stats grid columns
  const statsGridCols = isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)";

  const faqItems = [
    { q: "Is RealtyFlows really a free real estate CRM?", a: "Yes! Solo agents get the full CRM completely free — unlimited clients, visual pipeline, deal tracking, and communication history. You also get 2 free e-signings. Subscribe only when you want AI, automations, and additional signings. It's the best free CRM for real estate agents." },
    { q: "How is RealtyFlows different from Follow Up Boss, LionDesk, or kvCORE?", a: "Unlike Follow Up Boss, LionDesk, or kvCORE, RealtyFlows offers a genuinely free CRM tier for solo agents. We also have a unique agent-to-agent referral network, AI-powered lead scoring, and built-in e-signatures — all in one platform. Agents switching from those platforms often cite our modern interface, transparent real estate CRM pricing, and zero-commitment free plan." },
    { q: "What AI tools does RealtyFlows offer for real estate agents?", a: "RealtyFlows is an AI real estate CRM powered by Claude. It drafts personalized follow-ups, scores and prioritizes leads automatically, generates contracts, and handles automated follow-up sequences. It learns your communication style over time — the most advanced AI tools for real estate agents available today." },
    { q: "How does AI lead scoring work for real estate?", a: "Our real estate lead scoring software analyzes client engagement, response patterns, timeline signals, and property preferences. Every lead gets an AI-generated score so you always know who to call first. It's AI lead follow-up for real estate agents that actually saves you hours every week." },
    { q: "Is my data secure?", a: "Yes. We use bank-grade encryption, Google Cloud infrastructure, and never share your data with third parties. Your client information is yours — always." },
    { q: "Can I cancel anytime?", a: "Absolutely. Cancel anytime from your Settings page. Your plan stays active until the end of your billing period — no surprise charges." },
    { q: "Do you support teams and brokerages?", a: "Yes. Our Team and Agency plans include multiple seats, shared pipelines, agent management, and brokerage-wide analytics. Whether you need a CRM for a solo agent or an entire brokerage, our real estate agent CRM software scales with you. Enterprise plans include custom branding and API access." },
    { q: "What integrations do you support?", a: "Gmail, Google Calendar, DocuSign e-signatures, Stripe payments, and more. RealtyFlows connects to forty-plus platforms — making it the most integrated CRM for realtors available." },
    { q: "How do e-signings work?", a: "Create or upload a contract, add signature fields, and send it to your client. They sign electronically from any device. Signed PDFs are stored automatically in the deal — real estate e-signatures made simple." },
    { q: "Does RealtyFlows work for agents in Illinois?", a: "Absolutely. RealtyFlows was built in Chicago and is used by real estate agents across Illinois. Our CRM for real estate agents in Illinois includes everything you need — pipeline management, AI follow-ups, e-signatures, and local market intelligence." },
    { q: "How much does real estate CRM software actually cost?", a: "Most real estate CRMs charge $50–$300+/month per user. RealtyFlows is free for solo agents and starts at just $39/month for the Starter plan with AI and signings. Check our real estate CRM pricing section above for a full breakdown — we're the most affordable option with the most features." },
    { q: "What is the agent-to-agent network?", a: "It's a feature unique to RealtyFlows. Agents on the platform can connect directly to refer clients, co-broke deals, and collaborate on transactions. Think of it as a professional network built into your CRM — no other real estate CRM offers this." },
  ];

  function FAQItem({ question, answer }) {
    const [open, setOpen] = useState(false);
    return (
      <div style={{ borderBottom: `1px solid ${t.border}`, overflow: "hidden" }}>
        <button onClick={() => setOpen(!open)} style={{
          width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: isMobile ? "18px 0" : "22px 0", background: "none", border: "none",
          cursor: "pointer", textAlign: "left",
        }}>
          <span style={{ fontSize: isMobile ? 14 : 16, fontWeight: 500, color: t.navyLight, fontFamily: font.body, paddingRight: 16 }}>{question}</span>
          <span style={{
            fontSize: 22, color: t.gold, fontFamily: font.mono, flexShrink: 0,
            transition: "transform 0.3s", transform: open ? "rotate(45deg)" : "none",
          }}>+</span>
        </button>
        <div style={{
          maxHeight: open ? 300 : 0, opacity: open ? 1 : 0,
          transition: "max-height 0.4s ease, opacity 0.3s ease, padding 0.3s ease",
          padding: open ? "0 0 20px 0" : "0",
          overflow: "hidden",
        }}>
          <p style={{ fontSize: isMobile ? 13 : 14, color: t.textSecondary, fontFamily: font.body, lineHeight: 1.7, margin: 0 }}>{answer}</p>
        </div>
      </div>
    );
  }

  return (
    <div role="main" style={{ background: t.pageBg, fontFamily: font.body, color: t.textPrimary, overflowX: "hidden" }}>

      {/* Keyframes */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes dashboardFloat {
          0%, 100% { transform: perspective(1000px) rotateY(-5deg) translateY(0); }
          50% { transform: perspective(1000px) rotateY(-5deg) translateY(-10px); }
        }
        @keyframes typingDots {
          0%, 20% { opacity: 0.3; }
          50% { opacity: 1; }
          80%, 100% { opacity: 0.3; }
        }
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>

      {/* ==================== NAVBAR ==================== */}
      <nav aria-label="Main navigation" style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: isMobile ? "0 16px" : isTablet ? "0 24px" : "0 48px",
        height: isMobile ? 60 : 72,
        background: "rgba(250,248,243,0.92)",
        backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
        borderBottom: `1px solid ${t.borderGold || t.gold + "30"}`,
      }}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 10, cursor: "pointer" }} onClick={() => navigate("/")}>
          <img src="/logo.png" alt="RealtyFlows — Free AI-Powered Real Estate CRM" style={{ width: isMobile ? 28 : 32, height: isMobile ? 28 : 32 }} />
          <div style={{
            fontFamily: font.display, fontSize: isMobile ? 20 : 26, fontWeight: 600, letterSpacing: "-0.02em",
          }}>
            <span style={{ color: t.navyLight }}>Realty</span>
            <span style={{ color: t.gold }}>Flows</span>
          </div>
        </div>

        {/* Center Nav Links - desktop only */}
        {!isCompact && (
          <div style={{ display: "flex", gap: 36, alignItems: "center" }}>
            {navLinks.map(link => (
              <span key={link.label} style={{
                fontSize: 11, fontWeight: 500, letterSpacing: "2px", textTransform: "uppercase",
                color: t.textSecondary, cursor: "pointer", fontFamily: font.body,
                transition: "color 0.3s",
              }}
                onClick={() => link.href ? navigate(link.href) : document.getElementById(link.target)?.scrollIntoView({ behavior: "smooth" })}
                onMouseEnter={e => e.currentTarget.style.color = t.gold}
                onMouseLeave={e => e.currentTarget.style.color = t.textSecondary}
              >{link.label}</span>
            ))}
          </div>
        )}

        {/* Right: Login + CTA on desktop, Hamburger on compact */}
        {isCompact ? (
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              background: "none", border: "none", cursor: "pointer", padding: 8,
              display: "flex", flexDirection: "column", gap: 5, alignItems: "center", justifyContent: "center",
            }}
            aria-label="Toggle menu"
          >
            <div style={{
              width: 22, height: 2, background: t.navyLight, transition: "all 0.3s",
              transform: mobileMenuOpen ? "rotate(45deg) translateY(3.5px)" : "none",
            }} />
            <div style={{
              width: 22, height: 2, background: t.navyLight, transition: "all 0.3s",
              opacity: mobileMenuOpen ? 0 : 1,
            }} />
            <div style={{
              width: 22, height: 2, background: t.navyLight, transition: "all 0.3s",
              transform: mobileMenuOpen ? "rotate(-45deg) translateY(-3.5px)" : "none",
            }} />
          </button>
        ) : (
          <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
            <span onClick={() => navigate("/login")} style={{
              fontSize: 11, fontWeight: 500, letterSpacing: "2px", textTransform: "uppercase",
              color: t.textSecondary, cursor: "pointer", fontFamily: font.body,
              transition: "color 0.3s",
            }}
              onMouseEnter={e => e.currentTarget.style.color = t.gold}
              onMouseLeave={e => e.currentTarget.style.color = t.textSecondary}
            >Login</span>
            <button onClick={() => navigate("/signup")} style={{
              background: "transparent", border: `1px solid ${t.gold}`,
              padding: "10px 28px", fontSize: 11, fontWeight: 600, letterSpacing: "2px",
              textTransform: "uppercase", color: t.gold, cursor: "pointer",
              fontFamily: font.body, transition: "all 0.3s",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = t.gold; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = t.gold; }}
            >Get Started</button>
          </div>
        )}
      </nav>

      {/* ==================== MOBILE MENU DROPDOWN ==================== */}
      {isCompact && mobileMenuOpen && (
        <div style={{
          position: "fixed", top: isMobile ? 60 : 72, left: 0, right: 0, zIndex: 99,
          background: "rgba(250,248,243,0.98)",
          backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
          borderBottom: `1px solid ${t.borderGold || t.gold + "30"}`,
          padding: "16px 24px",
          display: "flex", flexDirection: "column", gap: 0,
          animation: "fadeUp 0.25s ease-out",
        }}>
          {navLinks.map(link => (
            <span key={link.label} style={{
              fontSize: 13, fontWeight: 500, letterSpacing: "2px", textTransform: "uppercase",
              color: t.textSecondary, cursor: "pointer", fontFamily: font.body,
              padding: "14px 0", borderBottom: `1px solid ${t.border}`,
              display: "block",
            }}
              onClick={() => {
                if (link.href) navigate(link.href);
                else document.getElementById(link.target)?.scrollIntoView({ behavior: "smooth" });
                setMobileMenuOpen(false);
              }}
            >{link.label}</span>
          ))}
          <span onClick={() => { navigate("/login"); setMobileMenuOpen(false); }} style={{
            fontSize: 13, fontWeight: 500, letterSpacing: "2px", textTransform: "uppercase",
            color: t.textSecondary, cursor: "pointer", fontFamily: font.body,
            padding: "14px 0", borderBottom: `1px solid ${t.border}`,
            display: "block",
          }}>Login</span>
          <button onClick={() => { navigate("/signup"); setMobileMenuOpen(false); }} style={{
            marginTop: 16, width: "100%", padding: "14px 0",
            background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`,
            border: "none", fontSize: 11, fontWeight: 600, letterSpacing: "2px",
            textTransform: "uppercase", color: "#fff", cursor: "pointer",
            fontFamily: font.body,
          }}>Get Started</button>
        </div>
      )}

      {/* ==================== HERO ==================== */}
      <section style={{
        position: "relative", minHeight: isMobile ? "auto" : "100vh",
        display: "flex", alignItems: "center",
        flexDirection: isCompact ? "column" : "row",
        padding: isMobile
          ? "100px 16px 48px"
          : isTablet
            ? "110px 32px 60px"
            : "120px 64px 80px",
        overflow: "hidden",
        backgroundImage: `radial-gradient(${t.gold}15 1px, transparent 1px)`,
        backgroundSize: "32px 32px",
      }}>
        {/* Interactive Product Preview - Desktop Dashboard Mockup */}
        {isDesktop && (
          <div style={{
            position: "absolute", right: "4%", top: "50%", transform: "translateY(-50%)",
            zIndex: 2, width: "min(380px, 35vw)", animation: "dashboardFloat 5s ease-in-out infinite",
          }}>
            <div style={{
              background: `linear-gradient(145deg, ${t.navyLight}, ${t.navyMid || "#1A3260"})`,
              borderRadius: 20, overflow: "hidden",
              boxShadow: `0 24px 64px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.08)`,
            }}>
              {/* Browser top bar */}
              <div style={{
                display: "flex", alignItems: "center", gap: 6, padding: "10px 14px",
                background: "rgba(0,0,0,0.2)", borderBottom: "1px solid rgba(255,255,255,0.06)",
              }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ff5f57" }} />
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#febc2e" }} />
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#28c840" }} />
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", fontFamily: font.mono, letterSpacing: "0.5px" }}>app.realtyflows.io</span>
              </div>

              {/* Dashboard header */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 18px", borderBottom: "1px solid rgba(255,255,255,0.06)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: `linear-gradient(135deg, ${t.gold}, ${t.goldLight || t.gold})`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700, color: "#fff",
                  }}>RF</div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#fff", fontFamily: font.body }}>Dashboard</span>
                </div>
                <div style={{
                  fontSize: 9, color: "rgba(255,255,255,0.4)", fontFamily: font.mono,
                  display: "flex", alignItems: "center", gap: 4,
                }}>
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 4px #22c55e80" }} />
                  Live
                </div>
              </div>

              {/* Mini stat cards */}
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8,
                padding: "14px 18px",
              }}>
                {[
                  { label: "Clients", value: "142", color: t.gold },
                  { label: "Deals", value: "28", color: "#22c55e" },
                  { label: "Revenue", value: "$2.4M", color: t.gold },
                ].map((stat, i) => (
                  <div key={i} style={{
                    background: "rgba(255,255,255,0.05)", borderRadius: 10,
                    padding: "10px 10px", border: "1px solid rgba(255,255,255,0.06)",
                  }}>
                    <div style={{ fontSize: 8, fontWeight: 500, color: "rgba(255,255,255,0.4)", fontFamily: font.body, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 4 }}>{stat.label}</div>
                    <div style={{ fontSize: 17, fontWeight: 600, color: stat.color, fontFamily: font.display }}>{stat.value}</div>
                  </div>
                ))}
              </div>

              {/* Mini pipeline bars */}
              <div style={{ padding: "6px 18px 14px" }}>
                <div style={{ fontSize: 9, fontWeight: 600, color: "rgba(255,255,255,0.35)", fontFamily: font.body, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 8 }}>Pipeline</div>
                <div style={{ display: "flex", gap: 4, height: 18, borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ flex: 4, background: t.gold, borderRadius: "3px 0 0 3px", opacity: 0.9 }} />
                  <div style={{ flex: 3, background: "#22c55e", opacity: 0.8 }} />
                  <div style={{ flex: 2, background: "#3b82f6", opacity: 0.8 }} />
                  <div style={{ flex: 1.5, background: "#a855f7", borderRadius: "0 3px 3px 0", opacity: 0.7 }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
                  {["Inquiry", "Active", "Offer", "Closed"].map((label, i) => (
                    <span key={i} style={{ fontSize: 7, color: "rgba(255,255,255,0.3)", fontFamily: font.body }}>{label}</span>
                  ))}
                </div>
              </div>

              {/* AI Assistant indicator */}
              <div style={{
                padding: "10px 18px 14px", borderTop: "1px solid rgba(255,255,255,0.06)",
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <div style={{
                  width: 22, height: 22, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${t.gold}, ${t.goldLight || t.gold})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10,
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff" stroke="none">
                    <path d="M12 2a4 4 0 014 4v2a4 4 0 01-8 0V6a4 4 0 014-4zM16 14H8a4 4 0 00-4 4v2h16v-2a4 4 0 00-4-4z" />
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 9, fontWeight: 600, color: t.gold, fontFamily: font.body, marginBottom: 2 }}>AI Assistant</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                    <span style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", fontFamily: font.body }}>Drafting follow-up</span>
                    <span style={{ display: "inline-flex", gap: 2 }}>
                      {[0, 1, 2].map(i => (
                        <span key={i} style={{
                          width: 3, height: 3, borderRadius: "50%", background: t.gold,
                          animation: `typingDots 1.4s ease-in-out ${i * 0.2}s infinite`,
                        }} />
                      ))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Large Navy Circle - right side (tablet only, scales with viewport) */}
        {isTablet && (() => {
          const circleSize = "45vw";
          const circleRight = "-15vw";
          return (
            <div style={{
              position: "absolute", right: circleRight, top: "50%", transform: "translateY(-50%)",
              width: circleSize, height: circleSize, maxWidth: 700, maxHeight: 700,
              minWidth: 300, minHeight: 300, borderRadius: "50%",
              background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`,
              zIndex: 1,
            }}>
              {/* Gold dashed arc SVG */}
              <svg style={{ position: "absolute", top: "-5%", left: "-5%", width: "110%", height: "110%" }} viewBox="0 0 780 780" preserveAspectRatio="xMidYMid meet">
                <circle cx="390" cy="390" r="370" fill="none" stroke={t.gold} strokeWidth="1" strokeDasharray="8 6" opacity="0.5" />
              </svg>
            </div>
          );
        })()}

        {/* Stats floating on circle (tablet only — desktop uses stats below hero) */}
        {isTablet && stats.length > 0 && (
          <div style={{
            position: "absolute", right: "3vw", top: "50%", transform: "translateY(-50%)",
            zIndex: 3,
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
            width: "36vw", maxWidth: 420,
          }}>
            {stats.map((stat, i) => (
              <div key={stat.label} style={{
                background: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.15)",
                padding: "14px 16px",
                animation: `float 4s ease-in-out ${i * 0.5}s infinite`,
              }}>
                <div style={{ width: 32, height: 2, background: t.gold, marginBottom: 8 }} />
                <div style={{ fontSize: 20, fontWeight: 300, color: "#fff", fontFamily: font.display, marginBottom: 4 }}>
                  {stat.rawValue ? <AnimatedCounter value={stat.rawValue} /> : stat.value}
                </div>
                <div style={{ fontSize: 8, fontWeight: 500, letterSpacing: "1px", textTransform: "uppercase", color: "rgba(255,255,255,0.6)", fontFamily: font.body }}>{stat.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Hero Text */}
        <div style={{
          maxWidth: isCompact ? "100%" : "50%",
          zIndex: 3,
          animation: "fadeUp 1s ease-out",
          textAlign: isMobile ? "center" : "left",
        }}>
          {/* Eyebrow */}
          <div style={{
            display: "flex", alignItems: "center", gap: isMobile ? 10 : 16,
            marginBottom: isMobile ? 20 : 32,
            justifyContent: isMobile ? "center" : "flex-start",
          }}>
            <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
            <span style={{
              fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: isMobile ? "2px" : "4px", textTransform: "uppercase",
              color: t.gold, fontFamily: font.body,
            }}>AI Real Estate CRM for Agents</span>
          </div>

          {/* Title */}
          <h1 style={{
            fontFamily: font.display, fontWeight: 300,
            fontSize: isMobile ? "clamp(32px, 8vw, 44px)" : "clamp(52px, 6vw, 84px)",
            lineHeight: 1.05,
            color: t.navyLight, marginBottom: isMobile ? 20 : 28,
          }}>
            The AI-Powered <em style={{ fontStyle: "italic", color: t.gold }}>CRM</em>
            <br />
            Real Estate Agents
            <br />
            <strong style={{ fontWeight: 700 }}>Actually Use</strong>
          </h1>

          {/* Subtitle */}
          <p style={{
            fontSize: isMobile ? 13 : 13, fontWeight: 300, lineHeight: 1.9, color: t.textSecondary,
            fontFamily: font.body, maxWidth: isMobile ? "100%" : 460,
            marginBottom: isMobile ? 32 : 44,
            margin: isMobile ? "0 auto" : undefined,
            marginBottom: isMobile ? 32 : 44,
          }}>
            The best CRM for real estate agents — free for solo agents. AI-powered lead scoring,
            automated follow-ups, real estate pipeline management, and the only
            agent-to-agent referral network. Built in Chicago, trusted nationwide.
          </p>

          {/* Value Props */}
          <div style={{
            display: "flex", gap: isMobile ? 8 : 12, marginBottom: isMobile ? 28 : 36,
            flexWrap: "wrap",
            justifyContent: isMobile ? "center" : "flex-start",
          }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: isMobile ? "8px 14px" : "10px 18px",
              background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid || "#1A3260"})`,
              border: `1px solid ${t.gold}40`,
              boxShadow: `0 4px 16px ${t.navyLight}20`,
            }}>
              <span style={{ fontSize: 14 }}>{"\uD83C\uDD93"}</span>
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase",
                color: "#fff", fontFamily: font.body,
              }}>Free CRM</span>
            </div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: isMobile ? "8px 14px" : "10px 18px",
              background: `linear-gradient(135deg, ${t.gold}, ${t.goldLight || t.gold})`,
              boxShadow: `0 4px 16px ${t.gold}30`,
            }}>
              <span style={{ fontSize: 14 }}>{"\uD83E\uDD1D"}</span>
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase",
                color: "#fff", fontFamily: font.body,
              }}>Agent-to-Agent Network</span>
            </div>
          </div>

          {/* Buttons */}
          <div style={{
            display: "flex", gap: 16, alignItems: "center",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: isMobile ? "center" : "flex-start",
            marginTop: 0,
          }}>
            <button onClick={() => navigate("/signup")} style={{
              background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`,
              border: "none", padding: isMobile ? "16px 0" : "16px 40px",
              width: isMobile ? "100%" : "auto",
              fontSize: 11, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase",
              color: "#fff", cursor: "pointer", fontFamily: font.body,
              transition: "transform 0.3s, box-shadow 0.3s",
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 30px ${t.navyLight}40`; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
            >Start Free</button>

            <button style={{
              background: "transparent", border: `1px solid ${t.border}`,
              padding: isMobile ? "16px 0" : "16px 32px",
              width: isMobile ? "100%" : "auto",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
              fontSize: 11, fontWeight: 500, letterSpacing: "2px", textTransform: "uppercase",
              color: t.textSecondary, cursor: "pointer", fontFamily: font.body,
              transition: "border-color 0.3s, color 0.3s",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = t.gold; e.currentTarget.style.color = t.gold; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.color = t.textSecondary; }}
            >
              <span style={{
                width: 28, height: 28, borderRadius: "50%", border: `1px solid ${t.gold}`,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, color: t.gold,
              }}>{"\u25B6"}</span>
              Watch Demo
            </button>
          </div>
        </div>

        {/* Mobile: condensed stats below hero text */}
        {isMobile && (
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12,
            marginTop: 40, width: "100%", zIndex: 3,
          }}>
            {stats.map((stat, i) => (
              <div key={stat.label} style={{
                background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`,
                padding: "16px 14px",
                borderLeft: `2px solid ${t.gold}`,
              }}>
                <div style={{ fontSize: 22, fontWeight: 300, color: "#fff", fontFamily: font.display, marginBottom: 2 }}>
                  {stat.rawValue ? <AnimatedCounter value={stat.rawValue} /> : stat.value}
                </div>
                <div style={{ fontSize: 9, fontWeight: 500, letterSpacing: "1px", textTransform: "uppercase", color: "rgba(255,255,255,0.6)", fontFamily: font.body }}>{stat.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tablet: compact stats row below hero */}
        {isTablet && (
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12,
            marginTop: 40, width: "100%", zIndex: 3,
          }}>
            {stats.map((stat, i) => (
              <div key={stat.label} style={{
                background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`,
                padding: "16px 14px",
                borderLeft: `2px solid ${t.gold}`,
              }}>
                <div style={{ fontSize: 24, fontWeight: 300, color: "#fff", fontFamily: font.display, marginBottom: 2 }}>
                  {stat.rawValue ? <AnimatedCounter value={stat.rawValue} /> : stat.value}
                </div>
                <div style={{ fontSize: 9, fontWeight: 500, letterSpacing: "1px", textTransform: "uppercase", color: "rgba(255,255,255,0.6)", fontFamily: font.body }}>{stat.label}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ==================== STATS BAR (desktop) ==================== */}
      {isDesktop && stats.length > 0 && (
        <section style={{
          background: t.navyLight, padding: "0 64px",
        }}>
          <div style={{
            maxWidth: 1200, margin: "0 auto",
            display: "grid", gridTemplateColumns: `repeat(${stats.length}, 1fr)`,
          }}>
            {stats.map((stat, i) => (
              <ScrollReveal key={stat.label} delay={i * 100}>
                <div style={{
                  padding: "32px 24px", textAlign: "center",
                  borderRight: i < stats.length - 1 ? "1px solid rgba(255,255,255,0.08)" : "none",
                }}>
                  <div style={{ fontSize: 32, fontWeight: 300, color: "#fff", fontFamily: font.display, marginBottom: 6 }}>
                    {stat.rawValue ? <AnimatedCounter value={stat.rawValue} /> : stat.value}
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: t.gold, fontFamily: font.body }}>{stat.label}</div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </section>
      )}

      {/* ==================== MARQUEE ==================== */}
      <div style={{
        background: t.navyLight, padding: isMobile ? "14px 0" : "18px 0", overflow: "hidden",
        borderTop: `1px solid ${t.gold}20`, borderBottom: `1px solid ${t.gold}20`,
      }}>
        <div style={{
          display: "flex", animation: "marquee 30s linear infinite", width: "fit-content",
        }}>
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} style={{
              fontSize: isMobile ? 10 : 11, fontWeight: 500, letterSpacing: isMobile ? "2px" : "3px", textTransform: "uppercase",
              color: t.gold, fontFamily: font.body, whiteSpace: "nowrap",
              padding: isMobile ? "0 20px" : "0 32px", opacity: 0.8,
            }}>
              {item}
              <span style={{ marginLeft: isMobile ? 20 : 32, opacity: 0.3 }}>{"\u2666"}</span>
            </span>
          ))}
        </div>
      </div>

      <WaveDivider color={t.pageBg} />

      {/* ==================== HOW IT WORKS ==================== */}
      <section style={{ background: t.pageBg, padding: sectionPadding }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <ScrollReveal>
            <div style={{ textAlign: "center", marginBottom: isMobile ? 36 : 64 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isMobile ? 10 : 16, marginBottom: 20 }}>
                <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
                <span style={{ fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: isMobile ? "2px" : "4px", textTransform: "uppercase", color: t.gold, fontFamily: font.body }}>How It Works</span>
                <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
              </div>
              <h2 style={{ fontFamily: font.display, fontSize: isMobile ? "clamp(24px, 6vw, 32px)" : "clamp(32px, 4vw, 48px)", fontWeight: 300, color: t.navyLight, lineHeight: 1.15 }}>
                Three Steps to <em style={{ fontStyle: "italic", color: t.gold }}>Closing</em> More Deals
              </h2>
            </div>
          </ScrollReveal>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: isMobile ? 24 : 0, position: "relative" }}>
            {/* Connecting line (desktop only) */}
            {!isMobile && (
              <div style={{
                position: "absolute", top: 44, left: "16%", right: "16%", height: 2,
                background: `linear-gradient(90deg, ${t.gold}40, ${t.gold}, ${t.gold}40)`,
                zIndex: 0,
              }} />
            )}
            {[
              { step: "01", title: "Sign Up Free", desc: "Create your account in 30 seconds. No credit card required. Import your existing clients or start fresh.", icon: "\uD83D\uDE80" },
              { step: "02", title: "Build Your Pipeline", desc: "Add clients, create deals, and organize everything in your visual pipeline. AI scores and prioritizes automatically.", icon: "\uD83D\uDCCA" },
              { step: "03", title: "Close More Deals", desc: "Let AI draft follow-ups, send contracts for e-signature, and track every milestone to closing.", icon: "\uD83C\uDFC6" },
            ].map((item, i) => (
              <ScrollReveal key={item.step} delay={i * 200}>
                <div style={{ textAlign: "center", padding: isMobile ? "0" : "0 24px", position: "relative", zIndex: 1 }}>
                  <div style={{
                    width: isMobile ? 64 : 88, height: isMobile ? 64 : 88, borderRadius: "50%", margin: "0 auto 20px",
                    background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid || "#1A3260"})`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: isMobile ? 28 : 36,
                    boxShadow: `0 8px 32px ${t.navyLight}30`,
                    border: `3px solid ${t.gold}40`,
                  }}>{item.icon}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "3px", color: t.gold, fontFamily: font.mono, marginBottom: 8 }}>STEP {item.step}</div>
                  <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 600, color: t.navyLight, fontFamily: font.display, marginBottom: 10 }}>{item.title}</div>
                  <p style={{ fontSize: 13, color: t.textSecondary, fontFamily: font.body, lineHeight: 1.7, maxWidth: 280, margin: "0 auto" }}>{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FREE CRM + AGENT-TO-AGENT ==================== */}
      <section style={{
        background: `linear-gradient(135deg, ${t.navyLight} 0%, ${t.navyMid || "#1A3260"} 100%)`,
        padding: sectionPadding, position: "relative", overflow: "hidden",
      }}>
        {/* Background pattern */}
        <div style={{ position: "absolute", inset: 0, opacity: 0.03, backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />

        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <ScrollReveal>
            <div style={{ textAlign: "center", marginBottom: isMobile ? 36 : 56 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isMobile ? 10 : 16, marginBottom: 20 }}>
                <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
                <span style={{ fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: isMobile ? "2px" : "4px", textTransform: "uppercase", color: t.gold, fontFamily: font.body }}>What Sets Us Apart</span>
                <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
              </div>
              <h2 style={{ fontFamily: font.display, fontSize: isMobile ? "clamp(24px, 6vw, 32px)" : "clamp(32px, 4vw, 48px)", fontWeight: 300, color: "#fff", lineHeight: 1.15 }}>
                Why Agents <em style={{ fontStyle: "italic", color: t.gold }}>Switch</em> to RealtyFlows
              </h2>
            </div>
          </ScrollReveal>

          <div style={{
            display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: isMobile ? 20 : 32,
          }}>
            {/* Free CRM Card */}
            <ScrollReveal delay={0}>
              <div style={{
                background: "rgba(255,255,255,0.06)", border: `1px solid rgba(255,255,255,0.1)`,
                padding: isMobile ? "32px 24px" : "44px 36px",
                transition: "all 0.4s", position: "relative", overflow: "hidden",
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = `${t.gold}60`; e.currentTarget.style.background = "rgba(255,255,255,0.09)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
              >
                <div style={{
                  position: "absolute", top: isMobile ? 20 : 28, right: isMobile ? 20 : 28,
                  background: `linear-gradient(135deg, ${t.gold}, ${t.goldLight || t.gold})`,
                  padding: "6px 14px", fontSize: 10, fontWeight: 700, letterSpacing: "1.5px",
                  textTransform: "uppercase", color: "#fff", fontFamily: font.body,
                }}>Free for Solo Agents</div>
                <div style={{ fontSize: isMobile ? 36 : 48, marginBottom: 16 }}>{"\uD83D\uDCB0"}</div>
                <div style={{
                  fontFamily: font.display, fontSize: isMobile ? 24 : 30, fontWeight: 600,
                  color: "#fff", marginBottom: 12,
                }}>Free CRM for Every Agent</div>
                <p style={{
                  fontSize: isMobile ? 13 : 14, color: "rgba(255,255,255,0.6)", fontFamily: font.body,
                  lineHeight: 1.8, marginBottom: 24,
                }}>
                  Solo agents get the full CRM — completely free. Manage unlimited clients, build your pipeline, track every deal, and run your business without paying a dime. Upgrade only when you want AI, automations, and e-signings.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {[
                    "Unlimited clients & contacts",
                    "Visual deal pipeline",
                    "Client communication history",
                    "Deal tracking & milestones",
                    "2 free e-signings included",
                  ].map(item => (
                    <div key={item} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: `${t.gold}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontSize: 10, color: t.gold }}>{"\u2713"}</span>
                      </div>
                      <span style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontFamily: font.body }}>{item}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => navigate("/signup")} style={{
                  marginTop: 28, width: "100%", padding: "14px 0",
                  background: `linear-gradient(135deg, ${t.gold}, ${t.goldLight || t.gold})`,
                  border: "none", fontSize: 11, fontWeight: 600, letterSpacing: "2px",
                  textTransform: "uppercase", color: "#fff", cursor: "pointer", fontFamily: font.body,
                  transition: "all 0.3s",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 24px ${t.gold}40`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                >Start Free — No Card Needed</button>
              </div>
            </ScrollReveal>

            {/* Agent-to-Agent Card */}
            <ScrollReveal delay={200}>
              <div style={{
                background: "rgba(255,255,255,0.06)", border: `1px solid rgba(255,255,255,0.1)`,
                padding: isMobile ? "32px 24px" : "44px 36px",
                transition: "all 0.4s", position: "relative", overflow: "hidden",
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = `${t.gold}60`; e.currentTarget.style.background = "rgba(255,255,255,0.09)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
              >
                <div style={{
                  position: "absolute", top: isMobile ? 20 : 28, right: isMobile ? 20 : 28,
                  background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid || "#1A3260"})`,
                  border: `1px solid ${t.gold}40`,
                  padding: "6px 14px", fontSize: 10, fontWeight: 700, letterSpacing: "1.5px",
                  textTransform: "uppercase", color: t.gold, fontFamily: font.body,
                }}>Exclusive</div>
                <div style={{ fontSize: isMobile ? 36 : 48, marginBottom: 16 }}>{"\uD83E\uDD1D"}</div>
                <div style={{
                  fontFamily: font.display, fontSize: isMobile ? 24 : 30, fontWeight: 600,
                  color: "#fff", marginBottom: 12,
                }}>Agent-to-Agent Network</div>
                <p style={{
                  fontSize: isMobile ? 13 : 14, color: "rgba(255,255,255,0.6)", fontFamily: font.body,
                  lineHeight: 1.8, marginBottom: 24,
                }}>
                  The only CRM where agents connect directly. Refer clients, co-broke deals, and build your professional network — all within one platform.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {[
                    "Refer clients to trusted agents",
                    "Co-broke deals seamlessly",
                    "In-platform agent messaging",
                    "Build your referral network",
                    "Shared deal collaboration",
                  ].map(item => (
                    <div key={item} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: `${t.gold}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontSize: 10, color: t.gold }}>{"\u2713"}</span>
                      </div>
                      <span style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontFamily: font.body }}>{item}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => navigate("/signup")} style={{
                  marginTop: 28, width: "100%", padding: "14px 0",
                  background: "transparent", border: `1px solid ${t.gold}`,
                  fontSize: 11, fontWeight: 600, letterSpacing: "2px",
                  textTransform: "uppercase", color: t.gold, cursor: "pointer", fontFamily: font.body,
                  transition: "all 0.3s",
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = t.gold; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = t.gold; }}
                >Join the Network</button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <WaveDivider color={t.surfaceWarm || t.pageBg} />

      {/* ==================== LIVE STATS + ACTIVITY FEED ==================== */}
      {hasRealData && <section style={{ background: t.surfaceWarm, padding: sectionPaddingSmall, borderBottom: `1px solid ${t.gold}15` }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          {/* Header */}
          <ScrollReveal>
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <div style={{
              fontFamily: font.display, fontSize: isMobile ? 22 : 28, fontWeight: 300,
              color: t.navyLight, marginBottom: 8,
            }}>
              Trusted by real estate professionals
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 6px #22c55e80", animation: "pulse 2s ease-in-out infinite" }} />
              <span style={{ fontSize: isMobile ? 10 : 11, color: t.textMuted, fontFamily: font.body }}>Updated every 5 minutes from live data</span>
            </div>
          </div>
          </ScrollReveal>

          {/* Stats Cards */}
          <div style={{
            display: "grid", gridTemplateColumns: statsGridCols, gap: isMobile ? 12 : 20,
            margin: isMobile ? "24px 0" : "40px 0",
          }}>
            {stats.map((stat, i) => (
              <ScrollReveal key={i} delay={i * 100}>
              <div style={{
                background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12,
                padding: isMobile ? "20px 16px" : "28px 24px", textAlign: "center",
                transition: "border-color 0.3s, box-shadow 0.3s",
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = `${t.gold}60`; e.currentTarget.style.boxShadow = `0 4px 20px ${t.gold}15`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.boxShadow = "none"; }}
              >
                <div style={{ fontSize: isMobile ? 20 : 24, marginBottom: 8 }}>{stat.icon}</div>
                <div style={{ fontFamily: font.display, fontSize: isMobile ? 24 : 32, fontWeight: 600, color: t.navyLight, marginBottom: 4 }}>
                  {stat.rawValue ? <AnimatedCounter value={stat.rawValue} /> : stat.value}
                </div>
                <div style={{ fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase", color: t.textMuted, fontFamily: font.body }}>{stat.label}</div>
              </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Live Activity Feed */}
          <div style={{
            background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12,
            padding: isMobile ? "16px 16px" : "20px 24px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 8px #22c55e80", animation: "pulse 2s ease-in-out infinite" }} />
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: t.textSecondary, fontFamily: font.body }}>Live Activity</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {activityFeed.map((item, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: isMobile ? "10px 0" : "12px 0",
                  borderTop: i > 0 ? `1px solid ${t.border}` : "none",
                  gap: 12,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <div style={{ width: 4, height: 4, borderRadius: "50%", background: t.gold, flexShrink: 0 }} />
                    <span style={{
                      fontSize: isMobile ? 12 : 13, color: t.textPrimary, fontFamily: font.body,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{item.action}</span>
                  </div>
                  <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono, flexShrink: 0 }}>{item.ago}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>}

      {/* ==================== FEATURES ==================== */}
      <section id="features" style={{ background: `linear-gradient(135deg, ${t.pageBg} 0%, #f0ebe3 50%, ${t.pageBg} 100%)`, padding: sectionPadding }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          {/* Section Header */}
          <ScrollReveal>
          <div style={{ textAlign: "center", marginBottom: isMobile ? 36 : 64 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isMobile ? 10 : 16, marginBottom: 20 }}>
              <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
              <span style={{
                fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: isMobile ? "2px" : "4px", textTransform: "uppercase",
                color: t.gold, fontFamily: font.body,
              }}>AI Tools for Real Estate Agents</span>
              <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
            </div>
            <h2 style={{
              fontFamily: font.display,
              fontSize: isMobile ? "clamp(24px, 6vw, 32px)" : "clamp(32px, 4vw, 48px)",
              fontWeight: 300, color: t.navyLight, lineHeight: 1.15,
            }}>
              Smart Lead Scoring That Tells You <em style={{ fontStyle: "italic", color: t.gold }}>Who to Call Next</em>
            </h2>
          </div>
          </ScrollReveal>

          {/* Feature Grid */}
          <div style={{ display: "grid", gridTemplateColumns: featureGridCols, gap: 1, background: t.border }}>
            {features.map((f, i) => (
              <ScrollReveal key={f.num} delay={i * 100}>
              <div style={{
                background: "rgba(255,255,255,0.7)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                border: `1px solid rgba(255,255,255,0.8)`,
                padding: isMobile ? "28px 20px" : "36px 28px",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                borderBottom: `3px solid transparent`,
                position: "relative",
                overflow: "hidden",
              }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = `0 12px 40px rgba(0,0,0,0.08)`;
                  e.currentTarget.style.borderBottom = `3px solid ${t.gold}`;
                  e.currentTarget.querySelector(".gold-bar").style.transform = "scaleX(1)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.borderBottom = "3px solid transparent";
                  e.currentTarget.querySelector(".gold-bar").style.transform = "scaleX(0)";
                }}
              >
                {/* Number */}
                <div style={{
                  fontFamily: font.display, fontSize: isMobile ? 32 : 42, fontWeight: 300, color: t.gold,
                  opacity: 0.3, marginBottom: 4, lineHeight: 1,
                }}>{f.num}</div>

                {/* Icon */}
                <div style={{ marginBottom: 16, color: t.navyLight, opacity: 0.7 }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    {f.icon}
                  </svg>
                </div>

                {/* Title */}
                <div style={{
                  fontFamily: font.display, fontSize: isMobile ? 18 : 22, fontWeight: 600, color: t.navyLight,
                  marginBottom: 12,
                }}>{f.title}</div>

                {/* Description */}
                <div style={{
                  fontSize: 12, fontWeight: 300, lineHeight: 1.8, color: t.textSecondary,
                  fontFamily: font.body,
                }}>{f.desc}</div>

                {/* Gold bottom bar on hover */}
                <div className="gold-bar" style={{
                  position: "absolute", bottom: 0, left: 0, right: 0, height: 2,
                  background: t.gold, transform: "scaleX(0)", transition: "transform 0.4s",
                  transformOrigin: "left",
                }} />
              </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <WaveDivider color={t.navyLight} />

      {/* ==================== VIDEO SECTION ==================== */}
      <section style={{ background: t.navyLight, padding: sectionPadding, position: "relative", overflow: "hidden" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <ScrollReveal>
            {/* Section header */}
            <div style={{ marginBottom: isMobile ? 24 : 40 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isMobile ? 10 : 16, marginBottom: 20 }}>
                <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
                <span style={{ fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: isMobile ? "2px" : "4px", textTransform: "uppercase", color: t.gold, fontFamily: font.body }}>See It In Action</span>
                <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
              </div>
              <h2 style={{ fontFamily: font.display, fontSize: isMobile ? "clamp(24px, 6vw, 32px)" : "clamp(32px, 4vw, 48px)", fontWeight: 300, color: "#fff", lineHeight: 1.15 }}>
                Watch How Agents <em style={{ fontStyle: "italic", color: t.gold }}>Win</em> With RealtyFlows
              </h2>
              <p style={{ fontSize: isMobile ? 13 : 15, color: "rgba(255,255,255,0.6)", fontFamily: font.body, marginTop: 12, maxWidth: 500, margin: "12px auto 0", lineHeight: 1.6 }}>
                A 60-second overview of how top agents use RealtyFlows to close more deals.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={200}>
            {/* Video placeholder - shows a stylized play button card */}
            <div style={{
              position: "relative", width: "100%", paddingBottom: "56.25%",
              background: `linear-gradient(135deg, ${t.navyMid || "#1A3260"}, ${t.navyLight})`,
              border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden", cursor: "pointer",
            }}
              onClick={() => {/* Future: open video modal or embed */}}
            >
              {/* Decorative grid lines */}
              <div style={{ position: "absolute", inset: 0, opacity: 0.05, backgroundImage: "linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />

              {/* Play button */}
              <div style={{
                position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                width: isMobile ? 64 : 80, height: isMobile ? 64 : 80, borderRadius: "50%",
                background: `linear-gradient(135deg, ${t.gold}, ${t.goldLight || t.gold})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: `0 8px 32px rgba(0,0,0,0.3), 0 0 60px ${t.gold}30`,
                transition: "transform 0.3s, box-shadow 0.3s",
              }}>
                <svg width={isMobile ? 24 : 28} height={isMobile ? 24 : 28} viewBox="0 0 24 24" fill="#fff">
                  <polygon points="8,5 20,12 8,19" />
                </svg>
              </div>

              {/* "Coming Soon" overlay text */}
              <div style={{
                position: "absolute", bottom: isMobile ? 16 : 24, left: "50%", transform: "translateX(-50%)",
                fontSize: 11, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase",
                color: "rgba(255,255,255,0.4)", fontFamily: font.body,
              }}>Demo Video Coming Soon</div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <WaveDivider color={t.pageBg} flip />

      {/* ==================== BEFORE/AFTER ==================== */}
      <section style={{ background: t.pageBg, padding: sectionPadding }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <ScrollReveal>
            <div style={{ textAlign: "center", marginBottom: isMobile ? 36 : 64 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isMobile ? 10 : 16, marginBottom: 20 }}>
                <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
                <span style={{ fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: isMobile ? "2px" : "4px", textTransform: "uppercase", color: t.gold, fontFamily: font.body }}>The Difference</span>
                <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
              </div>
              <h2 style={{ fontFamily: font.display, fontSize: isMobile ? "clamp(24px, 6vw, 32px)" : "clamp(32px, 4vw, 48px)", fontWeight: 300, color: t.navyLight, lineHeight: 1.15 }}>
                E-Signature & Deal Management <em style={{ fontStyle: "italic", color: t.gold }}>Built In</em>
              </h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={200}>
            <div style={{
              display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 0, border: `1px solid ${t.border}`, overflow: "hidden",
            }}>
              {/* Before */}
              <div style={{ padding: isMobile ? "28px 20px" : "40px 36px", background: "#f5f0eb" }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: t.coral || "#e74c3c", fontFamily: font.body, marginBottom: 20 }}>Without RealtyFlows</div>
                {[
                  { icon: "\u274C", text: "Leads tracked in spreadsheets and sticky notes" },
                  { icon: "\u274C", text: "Follow-ups forgotten \u2014 clients go cold" },
                  { icon: "\u274C", text: "Manual contract prep takes hours" },
                  { icon: "\u274C", text: "No real estate pipeline management \u2014 zero visibility" },
                  { icon: "\u274C", text: "No lead scoring \u2014 guessing who to call first" },
                  { icon: "\u274C", text: "Paying $300+/mo for bloated CRM software" },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 14 }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>{item.icon}</span>
                    <span style={{ fontSize: isMobile ? 13 : 14, color: t.textSecondary, fontFamily: font.body, lineHeight: 1.5 }}>{item.text}</span>
                  </div>
                ))}
              </div>
              {/* After */}
              <div style={{ padding: isMobile ? "28px 20px" : "40px 36px", background: t.surface, borderLeft: isMobile ? "none" : `1px solid ${t.border}`, borderTop: isMobile ? `1px solid ${t.border}` : "none" }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: t.emerald, fontFamily: font.body, marginBottom: 20 }}>With RealtyFlows</div>
                {[
                  { icon: "\u2705", text: "AI real estate CRM with automatic lead scoring" },
                  { icon: "\u2705", text: "Automated follow-up \u2014 AI drafts and sends for you" },
                  { icon: "\u2705", text: "Contracts generated and e-signed in minutes" },
                  { icon: "\u2705", text: "Real estate pipeline management with live analytics" },
                  { icon: "\u2705", text: "Agent-to-agent referral network built in" },
                  { icon: "\u2705", text: "Free CRM for solo agents \u2014 no credit card needed" },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 14 }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>{item.icon}</span>
                    <span style={{ fontSize: isMobile ? 13 : 14, color: t.textPrimary, fontFamily: font.body, lineHeight: 1.5, fontWeight: 500 }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ==================== SHOWCASE / PIPELINE ==================== */}
      <section id="pipeline" style={{
        padding: sectionPadding, position: "relative", overflow: "hidden",
        background: `linear-gradient(180deg, ${t.pageBg}, ${t.surfaceWarm})`,
      }}>
        {/* Watermark */}
        <div style={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          fontFamily: font.display, fontSize: isMobile ? "80px" : "clamp(120px, 20vw, 300px)", fontWeight: 700,
          color: t.gold, opacity: 0.04, letterSpacing: "0.1em", whiteSpace: "nowrap",
          userSelect: "none", pointerEvents: "none",
        }}>FLOWS</div>

        <div style={{
          maxWidth: 1200, margin: "0 auto",
          display: "flex", flexDirection: isCompact ? "column" : "row",
          gap: isMobile ? 36 : isTablet ? 48 : 80,
          alignItems: isCompact ? "stretch" : "center",
          position: "relative", zIndex: 1,
        }}>
          {/* Left Side */}
          <div style={{ flex: 1 }}>
            <ScrollReveal direction="left">
            {/* Section Header */}
            <div style={{
              display: "flex", alignItems: "center", gap: isMobile ? 10 : 16, marginBottom: 20,
              justifyContent: isMobile ? "center" : "flex-start",
            }}>
              <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
              <span style={{
                fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: isMobile ? "2px" : "4px", textTransform: "uppercase",
                color: t.gold, fontFamily: font.body,
              }}>Real Estate Pipeline Management Software</span>
            </div>
            <h2 style={{
              fontFamily: font.display,
              fontSize: isMobile ? "clamp(24px, 6vw, 32px)" : "clamp(32px, 4vw, 48px)",
              fontWeight: 300, color: t.navyLight, lineHeight: 1.15, marginBottom: 24,
              textAlign: isMobile ? "center" : "left",
            }}>
              Automated Follow-Up That <em style={{ fontStyle: "italic", color: t.gold }}>Never</em> Lets a Client Fall Through
            </h2>
            <p style={{
              fontSize: 13, fontWeight: 300, lineHeight: 1.9, color: t.textSecondary,
              fontFamily: font.body, marginBottom: isMobile ? 28 : 40,
              maxWidth: isCompact ? "100%" : 420,
              textAlign: isMobile ? "center" : "left",
            }}>
              From first inquiry to closing table, every stage of your pipeline is
              meticulously tracked and intelligently managed.
            </p>

            {/* Pipeline feature list */}
            {["Real-time deal tracking", "Automated stage progression", "Revenue forecasting", "Team performance metrics"].map(item => (
              <div key={item} style={{
                display: "flex", alignItems: "center", gap: 14, marginBottom: 16,
                justifyContent: isMobile ? "center" : "flex-start",
              }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: t.gold, flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 400, color: t.textSecondary, fontFamily: font.body }}>{item}</span>
              </div>
            ))}
            </ScrollReveal>
          </div>

          {/* Right Side: Pipeline Stage Cards */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
            {pipelineStages.map((stage, i) => (
              <ScrollReveal key={stage.name} delay={i * 100} direction="right">
              <div style={{
                display: "flex", alignItems: "center", gap: isMobile ? 14 : 20,
                background: t.surface, padding: isMobile ? "16px 16px" : "20px 24px",
                borderLeft: `3px solid ${t.gold}`,
                boxShadow: `0 2px 12px ${t.shadow}`,
                transition: "transform 0.3s, box-shadow 0.3s",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateX(8px)"; e.currentTarget.style.boxShadow = `0 4px 20px ${t.shadow}`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateX(0)"; e.currentTarget.style.boxShadow = `0 2px 12px ${t.shadow}`; }}
              >
                <div style={{ fontSize: isMobile ? 20 : 24 }}>{stage.emoji}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: t.navyLight, fontFamily: font.body }}>{stage.name}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>{stage.count} deals</div>
                </div>
                <div style={{ fontFamily: font.display, fontSize: isMobile ? 18 : 22, fontWeight: 600, color: t.gold, flexShrink: 0 }}>{stage.value}</div>
              </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== PRICING ==================== */}
      <section id="pricing" style={{ background: `linear-gradient(180deg, ${t.pageBg} 0%, #f5f0e8 100%)`, padding: sectionPadding }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          {/* Section Header */}
          <ScrollReveal>
          <div style={{ textAlign: "center", marginBottom: isMobile ? 36 : 64 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isMobile ? 10 : 16, marginBottom: 20 }}>
              <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
              <span style={{
                fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: isMobile ? "2px" : "4px", textTransform: "uppercase",
                color: t.gold, fontFamily: font.body,
              }}>Real Estate CRM Pricing</span>
              <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
            </div>
            <h2 style={{
              fontFamily: font.display,
              fontSize: isMobile ? "clamp(24px, 6vw, 32px)" : "clamp(32px, 4vw, 48px)",
              fontWeight: 300, color: t.navyLight, lineHeight: 1.15, marginBottom: 24,
            }}>
              Built for Solo Agents and <em style={{ fontStyle: "italic", color: t.gold }}>Growing Teams</em>
            </h2>

            {/* Monthly / Annual Toggle */}
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 0,
              background: t.pageBg, borderRadius: 99, padding: 3,
              border: `1px solid ${t.border}`,
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}>
              <button onClick={() => setPricingInterval("monthly")} style={{
                padding: isMobile ? "10px 18px" : "10px 24px", borderRadius: 99, border: "none", cursor: "pointer",
                fontSize: 13, fontWeight: 600, fontFamily: font.body, transition: "all 0.2s",
                background: !isAnnual ? t.navyLight : "transparent",
                color: !isAnnual ? "#fff" : t.textMuted,
              }}>Monthly</button>
              <button onClick={() => setPricingInterval("annual")} style={{
                padding: isMobile ? "10px 18px" : "10px 24px", borderRadius: 99, border: "none", cursor: "pointer",
                fontSize: 13, fontWeight: 600, fontFamily: font.body, transition: "all 0.2s",
                background: isAnnual ? t.navyLight : "transparent",
                color: isAnnual ? "#fff" : t.textMuted,
              }}>Annual <span style={{ fontSize: 10, fontWeight: 700, color: isAnnual ? t.gold : t.emerald, marginLeft: 4 }}>2 Months Free</span></button>
            </div>
          </div>
          </ScrollReveal>

          {/* Solo Agent Plans */}
          <ScrollReveal>
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 16, marginBottom: 24 }}>
            <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "3px", textTransform: "uppercase", color: t.gold, fontFamily: font.body, whiteSpace: "nowrap" }}>Solo Agent</span>
            <div style={{ flex: 1, height: 1, background: `${t.gold}30` }} />
          </div>
          <div style={{
            display: "grid", gridTemplateColumns: soloGridCols,
            gap: isMobile ? 16 : 24, maxWidth: isMobile ? "100%" : 700,
            margin: "0 auto 48px", alignItems: "stretch",
          }}>
            {soloAgentPlans.map(plan => (
              <PricingCard key={plan.tier} plan={plan} navigate={navigate} isAnnual={isAnnual} isMobile={isMobile} />
            ))}
          </div>
          </ScrollReveal>

          {/* Team Plans */}
          <ScrollReveal delay={200}>
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 16, marginBottom: 24 }}>
            <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "3px", textTransform: "uppercase", color: t.gold, fontFamily: font.body }}>Team</span>
            <div style={{ flex: 1, height: 1, background: `${t.gold}30` }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: teamGridCols, gap: isMobile ? 16 : 24, alignItems: "stretch" }}>
            {teamPricingPlans.map(plan => (
              <PricingCard key={plan.tier} plan={plan} navigate={navigate} isAnnual={isAnnual} isMobile={isMobile} />
            ))}
          </div>
          </ScrollReveal>

          {/* Need more signings? */}
          <div style={{
            marginTop: 48, textAlign: "center", padding: isMobile ? "28px 20px" : "36px 40px",
            background: `${t.navyLight}08`, border: `1px solid ${t.navyLight}15`,
            borderRadius: 0,
          }}>
            <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 300, color: t.navyLight, fontFamily: font.display, marginBottom: 8 }}>
              Need more <em style={{ fontStyle: "italic", color: t.gold }}>signings</em>?
            </div>
            <div style={{ fontSize: 13, color: t.textMuted, fontFamily: font.body, maxWidth: 500, margin: "0 auto 20px", lineHeight: 1.6 }}>
              Purchase add-on signing packs anytime. No plan change required.
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: isMobile ? 12 : 24, flexWrap: "wrap" }}>
              {[
                { label: "Single Signing", price: "$9" },
                { label: "Pack of 10", price: "$75", save: "Save 17%" },
                { label: "Pack of 25", price: "$175", save: "Save 22%" },
              ].map(addon => (
                <div key={addon.label} style={{
                  padding: "16px 24px", background: t.surface, border: `1px solid ${t.border}`,
                  borderRadius: 0, minWidth: 140, textAlign: "center",
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, fontFamily: font.body, marginBottom: 4 }}>{addon.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 300, color: t.navyLight, fontFamily: font.display }}>{addon.price}</div>
                  {addon.save && <div style={{ fontSize: 10, fontWeight: 600, color: t.emerald, fontFamily: font.body, marginTop: 4 }}>{addon.save}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== TESTIMONIALS ==================== */}
      <section style={{ background: t.pageBg, padding: sectionPadding }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          {/* Section Header */}
          <ScrollReveal>
          <div style={{ textAlign: "center", marginBottom: isMobile ? 36 : 64 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isMobile ? 10 : 16, marginBottom: 20 }}>
              <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
              <span style={{
                fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: isMobile ? "2px" : "4px", textTransform: "uppercase",
                color: t.gold, fontFamily: font.body,
              }}>Recent Testimonials</span>
              <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
            </div>
            <h2 style={{
              fontFamily: font.display,
              fontSize: isMobile ? "clamp(24px, 6vw, 32px)" : "clamp(32px, 4vw, 48px)",
              fontWeight: 300, color: t.navyLight, lineHeight: 1.15,
            }}>
              Trusted by the <em style={{ fontStyle: "italic", color: t.gold }}>Elite</em>
            </h2>
          </div>
          </ScrollReveal>

          {/* Satisfaction Summary */}
          {(() => {
            const sat = liveStats?.satisfaction?.total_reviews > 0
              ? liveStats.satisfaction
              : { rate: 100, avg_rating: 4.9, total_reviews: 4 };
            return (
              <div style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "center",
                gap: isMobile ? 20 : 48,
                marginBottom: isMobile ? 32 : 48,
                padding: isMobile ? "20px 0" : "24px 0",
                borderTop: `1px solid ${t.border}`, borderBottom: `1px solid ${t.border}`,
                alignItems: "center",
              }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: font.mono, fontSize: isMobile ? 26 : 32, fontWeight: 800, color: t.navyLight }}>{sat.rate}%</div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, letterSpacing: "1px", textTransform: "uppercase", marginTop: 4 }}>Satisfaction Rate</div>
                </div>
                <div style={{
                  width: isMobile ? 60 : 1,
                  height: isMobile ? 1 : "auto",
                  alignSelf: isMobile ? "center" : "stretch",
                  background: t.border,
                }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: font.mono, fontSize: isMobile ? 26 : 32, fontWeight: 800, color: t.navyLight }}>
                    {sat.avg_rating}<span style={{ fontSize: isMobile ? 14 : 18, color: t.gold }}>/5</span>
                  </div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, letterSpacing: "1px", textTransform: "uppercase", marginTop: 4 }}>Avg Rating</div>
                </div>
                <div style={{
                  width: isMobile ? 60 : 1,
                  height: isMobile ? 1 : "auto",
                  alignSelf: isMobile ? "center" : "stretch",
                  background: t.border,
                }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: font.mono, fontSize: isMobile ? 26 : 32, fontWeight: 800, color: t.navyLight }}>{sat.total_reviews}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, letterSpacing: "1px", textTransform: "uppercase", marginTop: 4 }}>Reviews</div>
                </div>
              </div>
            );
          })()}

          {/* Testimonial Cards — auto-rotating */}
          <div key={testimonialPage} style={{
            display: "grid", gridTemplateColumns: testimonialGridCols, gap: isMobile ? 16 : 24,
            animation: "testimonialFade 0.5s ease",
          }}>
            <style>{`@keyframes testimonialFade { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
            {testimonials.map((test, i) => (
              <ScrollReveal key={test.author + testimonialPage} delay={i * 150}>
              <div style={{
                background: t.surface, padding: isMobile ? "28px 20px" : "40px 32px",
                border: `1px solid ${t.border}`,
                transition: "box-shadow 0.3s",
              }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = `0 8px 32px ${t.shadow}`}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
              >
                <div style={{ fontSize: 48, fontFamily: font.display, color: `${t.gold}20`, lineHeight: 1, marginBottom: -12 }}>{"\u201C"}</div>
                {/* Stars + Date */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                  <div style={{ display: "flex", gap: 4 }}>
                    {[1,2,3,4,5].map(s => (
                      <span key={s} style={{ color: s <= (test.rating || 5) ? t.gold : t.border, fontSize: 14 }}>{"\u2605"}</span>
                    ))}
                  </div>
                  <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body, letterSpacing: "0.5px" }}>{test.date}</span>
                </div>

                {/* Quote */}
                <p style={{
                  fontFamily: font.display, fontSize: isMobile ? 16 : 18, fontWeight: 400, fontStyle: "italic",
                  color: t.navyLight, lineHeight: 1.6, marginBottom: 28,
                }}>
                  "{test.quote}"
                </p>

                {/* Author */}
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{
                    width: isMobile ? 38 : 44, height: isMobile ? 38 : 44, borderRadius: "50%",
                    background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`,
                    border: `2px solid ${t.gold}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 600, color: "#fff", fontFamily: font.body, flexShrink: 0,
                  }}>{test.avatar}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.navyLight, fontFamily: font.body }}>{test.author}</div>
                    <div style={{
                      fontSize: 11, color: t.textMuted, fontFamily: font.body,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{test.role}</div>
                  </div>
                </div>
              </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Pagination dots */}
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 24 }}>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button key={i} onClick={() => setTestimonialPage(i)} style={{
                  width: i === testimonialPage ? 24 : 8, height: 8, borderRadius: 4, border: "none",
                  background: i === testimonialPage ? t.gold : `${t.gold}40`,
                  cursor: "pointer", transition: "all 0.3s", padding: 0,
                }} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section style={{
        background: `linear-gradient(135deg, ${t.navyLight} 0%, ${t.navyMid || "#1A3260"} 100%)`,
        padding: isMobile ? "64px 16px" : "100px 64px",
        textAlign: "center", position: "relative", overflow: "hidden",
      }}>
        {/* Background pattern */}
        <div style={{ position: "absolute", inset: 0, opacity: 0.03, backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />

        <div style={{ maxWidth: 700, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <ScrollReveal>
            <div style={{
              fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: isMobile ? "2px" : "4px",
              textTransform: "uppercase", color: t.gold, fontFamily: font.body, marginBottom: 20,
            }}>Ready to Transform Your Business?</div>
            <h2 style={{
              fontFamily: font.display,
              fontSize: isMobile ? "clamp(28px, 7vw, 36px)" : "clamp(36px, 5vw, 56px)",
              fontWeight: 300, color: "#fff", lineHeight: 1.15, marginBottom: 20,
            }}>
              Start Closing More <em style={{ fontStyle: "italic", color: t.gold }}>Deals</em> Today
            </h2>
            <p style={{
              fontSize: isMobile ? 14 : 16, color: "rgba(255,255,255,0.6)", fontFamily: font.body,
              lineHeight: 1.7, maxWidth: 500, margin: "0 auto 36px",
            }}>
              Join hundreds of real estate agents who've switched to the best AI CRM for realtors. Free for solo agents — no credit card, no commitment.
            </p>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", flexDirection: isMobile ? "column" : "row", alignItems: "center" }}>
              <button onClick={() => navigate("/signup")} style={{
                background: `linear-gradient(135deg, ${t.gold}, ${t.goldLight || t.gold})`,
                border: "none", padding: isMobile ? "16px 40px" : "18px 56px",
                fontSize: 12, fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase",
                color: "#fff", cursor: "pointer", fontFamily: font.body,
                boxShadow: `0 8px 32px ${t.gold}40`,
                transition: "all 0.3s",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = `0 12px 40px ${t.gold}60`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 8px 32px ${t.gold}40`; }}
              >Start Free Now</button>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", fontFamily: font.body }}>No credit card required</span>
            </div>

            {/* Trust badges */}
            <div style={{
              display: "flex", justifyContent: "center", gap: isMobile ? 16 : 32,
              marginTop: isMobile ? 32 : 48, flexWrap: "wrap",
            }}>
              {[
                { icon: "\uD83D\uDD12", text: "We never sell your data" },
                { icon: "\uD83D\uDEE1\uFE0F", text: "Bank-grade encryption" },
                { icon: "\uD83D\uDC64", text: "Your data stays yours" },
              ].map(badge => (
                <div key={badge.text} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>{badge.icon}</span>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontFamily: font.body, fontWeight: 500 }}>{badge.text}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section id="faq" style={{ background: t.surface, padding: sectionPadding }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <ScrollReveal>
            <div style={{ textAlign: "center", marginBottom: isMobile ? 36 : 64 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isMobile ? 10 : 16, marginBottom: 20 }}>
                <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
                <span style={{ fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: isMobile ? "2px" : "4px", textTransform: "uppercase", color: t.gold, fontFamily: font.body }}>FAQ</span>
                <div style={{ width: isMobile ? 24 : 40, height: 1, background: t.gold }} />
              </div>
              <h2 style={{ fontFamily: font.display, fontSize: isMobile ? "clamp(24px, 6vw, 32px)" : "clamp(32px, 4vw, 48px)", fontWeight: 300, color: t.navyLight, lineHeight: 1.15 }}>
                Common <em style={{ fontStyle: "italic", color: t.gold }}>Questions</em>
              </h2>
            </div>
          </ScrollReveal>
          {faqItems.map((faq, i) => (
            <ScrollReveal key={i} delay={i * 80}>
              <FAQItem question={faq.q} answer={faq.a} />
            </ScrollReveal>
          ))}
        </div>
      </section>

      <WaveDivider color={t.navyLight} />

      {/* ==================== FOOTER ==================== */}
      <footer style={{
        background: t.navyLight, padding: isMobile ? "48px 16px 24px" : "64px 64px 32px",
        color: "rgba(255,255,255,0.7)",
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "2fr 1fr 1fr 1fr",
            gap: isMobile ? 32 : 48,
            marginBottom: isMobile ? 32 : 48,
          }}>
            {/* Brand */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <img src="/logo.png" alt="RealtyFlows — Free AI-Powered Real Estate CRM" style={{ width: 28, height: 28, filter: "brightness(2)" }} />
                <span style={{ fontFamily: font.display, fontSize: 22, fontWeight: 600, color: "#fff" }}>
                  Realty<span style={{ color: t.gold }}>Flows</span>
                </span>
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.7, fontFamily: font.body, color: "rgba(255,255,255,0.5)", maxWidth: 280 }}>
                The best AI-powered real estate CRM — free for solo agents. Pipeline management, automated follow-ups, lead scoring, e-signatures, and the only agent-to-agent network. Built in Chicago, Illinois.
              </p>
            </div>

            {/* Product */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: t.gold, marginBottom: 16, fontFamily: font.body }}>Product</div>
              {[
                { label: "Pipeline", target: "pipeline" },
                { label: "AI Assistant", target: "features" },
                { label: "E-Signatures", target: "features" },
                { label: "Automations", target: "features" },
                { label: "Team Management", target: "pricing" },
              ].map(item => (
                <div key={item.label} onClick={() => document.getElementById(item.target)?.scrollIntoView({ behavior: "smooth" })}
                  style={{ fontSize: 13, fontFamily: font.body, marginBottom: 10, cursor: "pointer", transition: "color 0.2s", color: "rgba(255,255,255,0.5)" }}
                  onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                  onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.5)"}
                >{item.label}</div>
              ))}
            </div>

            {/* Company */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: t.gold, marginBottom: 16, fontFamily: font.body }}>Company</div>
              {[
                { label: "Pricing", action: () => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" }) },
                { label: "Contact", action: () => navigate("/contact") },
                { label: "Terms", action: () => navigate("/terms") },
                { label: "Privacy", action: () => navigate("/privacy") },
                { label: "AI Transparency", action: () => document.getElementById("ai-transparency")?.scrollIntoView({ behavior: "smooth" }) },
              ].map(item => (
                <div key={item.label} onClick={item.action} style={{ fontSize: 13, fontFamily: font.body, marginBottom: 10, cursor: "pointer", color: "rgba(255,255,255,0.5)", transition: "color 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                  onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.5)"}
                >{item.label}</div>
              ))}
            </div>

            {/* Get Started */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: t.gold, marginBottom: 16, fontFamily: font.body }}>Get Started</div>
              <p style={{ fontSize: 13, lineHeight: 1.7, fontFamily: font.body, color: "rgba(255,255,255,0.5)", marginBottom: 16 }}>
                Start free. No credit card required.
              </p>
              <button onClick={() => navigate("/signup")} style={{
                padding: "12px 32px", background: `linear-gradient(135deg, ${t.gold}, ${t.goldLight})`,
                border: "none", color: "#fff", fontSize: 11, fontWeight: 600, letterSpacing: "2px",
                textTransform: "uppercase", cursor: "pointer", fontFamily: font.body, transition: "all 0.3s",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.3)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
              >Create Account</button>
            </div>
          </div>

          {/* AI Transparency Disclosure */}
          <div id="ai-transparency" style={{
            borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 20, marginTop: 12, marginBottom: 12,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: t.gold, marginBottom: 10, fontFamily: font.body }}>
              AI Transparency
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", fontFamily: font.body, lineHeight: 1.7, maxWidth: 700 }}>
              RealtyFlows uses <strong style={{ color: "rgba(255,255,255,0.65)" }}>Ollama</strong> with the <strong style={{ color: "rgba(255,255,255,0.65)" }}>Llama 3.2 model</strong> to power its AI features, including the AI chatbot assistant (Rio), AI document generation, and client scoring. All AI processing runs on our own infrastructure — no user data is sent to third-party AI providers. Gmail data accessed via the Gmail API is used solely for email management and is never processed by AI models.
            </div>
          </div>

          {/* Bottom bar */}
          <div style={{
            borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: isMobile ? 20 : 24,
            display: "flex", flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between", alignItems: isMobile ? "center" : "center",
            gap: 12,
          }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", fontFamily: font.body }}>
              &copy; {new Date().getFullYear()} RealtyFlows. All rights reserved.
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", fontFamily: font.body }}>
              Built with care in Chicago
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}

/* ==================== SVG ICON PATHS ==================== */

const pipelineIcon = (
  <>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </>
);

const aiIcon = (
  <>
    <path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z" />
    <path d="M16 14H8a4 4 0 0 0-4 4v2h16v-2a4 4 0 0 0-4-4z" />
    <circle cx="12" cy="6" r="1.5" />
  </>
);

const analyticsIcon = (
  <>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </>
);

const automationIcon = (
  <>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </>
);

const integrationIcon = (
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v4M12 19v4M4.2 4.2l2.8 2.8M17 17l2.8 2.8M1 12h4M19 12h4M4.2 19.8l2.8-2.8M17 7l2.8-2.8" />
  </>
);

const agentNetworkIcon = (
  <>
    <circle cx="7" cy="6" r="3" />
    <circle cx="17" cy="6" r="3" />
    <path d="M4 14a4 4 0 0 1 6 0M14 14a4 4 0 0 1 6 0" />
    <path d="M7 10v2M17 10v2" />
    <path d="M9 17h6" strokeDasharray="2 2" />
    <circle cx="12" cy="19" r="2" />
  </>
);

const freeIcon = (
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M9 12h6M9 9h4M9 15h5" />
  </>
);

const securityIcon = (
  <>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </>
);
